import { DateTime } from 'luxon';
import axios from 'smg/axios';
import * as models from 'smg/customer-api/models';
import * as html from 'smg/html';
import { Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { AccountReady } from '../account-session';
import { Relref } from '../hugo';
import { FinishedQuoteLink, TicketEffectiveStatusID } from '../quote';

type statusFilter = '' | 'queue' | 'progress' | 'art' | 'sent' | 'canceled';

const pageSizes = [10, 25, 50];
const defaultPageSize = 10;
const refreshMs = 550;
// estimatedCompletion/estimatedHoursRemaining aren't data fields — the
// endpoint's process func computes them onto every non-terminal row.
const dataFields = [
    'id', 'key', 'created', 'quoteRequestProjectTaskStatusID',
    'quoteRequestProjectTaskTypeID', 'quoteRequestProjectTaskPriorityID',
    'changelog', 'quoteProof', 'assignedUser', 'lastUserEvent', 'listSummary',
].join(',');

const inProgress = { label: 'In Progress', badge: 'bg-primary-subtle text-primary-emphasis', filter: 'progress' as statusFilter };
const statusDisplay: Record<number, { label: string; badge: string; filter: statusFilter }> = {
    [models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_IN_PROGRESS]: inProgress,
    [models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_PAUSED]: inProgress,
    [models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE]: { label: 'Art Done', badge: 'bg-success-subtle text-success-emphasis', filter: 'art' },
    [models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_SENT]: { label: 'Quote Sent', badge: 'bg-secondary-subtle text-secondary-emphasis', filter: 'sent' },
    [models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_CUSTOMER_CANCELED]: { label: 'Canceled', badge: 'bg-light text-body-tertiary', filter: 'canceled' },
};
const inTheQueue = { label: 'In the Queue', badge: 'bg-light text-body-secondary', filter: 'queue' as statusFilter };

const typeLabels: Record<number, string> = {
    [models.QuoteRequestProjectTaskTypeID.QUOTE_REQUEST_PROJECT_TASK_TYPES_REVISION]: 'Revision',
    [models.QuoteRequestProjectTaskTypeID.QUOTE_REQUEST_PROJECT_TASK_TYPES_NEW]: 'New',
    [models.QuoteRequestProjectTaskTypeID.QUOTE_REQUEST_PROJECT_TASK_TYPES_SMALL_REVISION]: 'Small Revision',
    [models.QuoteRequestProjectTaskTypeID.QUOTE_REQUEST_PROJECT_TASK_TYPES_LARGE_REVISION]: 'Large Revision',
};

const statusFilters: { filter: statusFilter; label: string }[] = [
    { filter: '', label: 'All' },
    { filter: 'queue', label: 'In the Queue' },
    { filter: 'progress', label: 'In Progress' },
    { filter: 'art', label: 'Art Done' },
    { filter: 'sent', label: 'Quote Sent' },
    { filter: 'canceled', label: 'Canceled' },
];

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const $root = document.querySelector('#dt-root') as HTMLElement;
    const $subtitle = document.querySelector('#dt-subtitle') as HTMLElement;
    const $loading = document.querySelector('#dt-loading') as HTMLElement;
    const $error = document.querySelector('#dt-error') as HTMLElement;
    const $empty = document.querySelector('#dt-empty') as HTMLElement;
    const $main = document.querySelector('#dt-main') as HTMLElement;
    const $chips = document.querySelector('#dt-chips') as HTMLElement;
    const $search = document.querySelector('#dt-search') as HTMLInputElement;
    const $sort = document.querySelector('#dt-sort') as HTMLSelectElement;
    const $pageSize = document.querySelector('#dt-page-size') as HTMLSelectElement;
    const $list = document.querySelector('#dt-list') as HTMLElement;
    const $refresh = document.querySelector('#dt-refresh') as HTMLElement;
    const $noResults = document.querySelector('#dt-no-results') as HTMLElement;
    const $clearFilters = document.querySelector('#dt-clear-filters') as HTMLButtonElement;
    const $pager = document.querySelector('#dt-pager') as HTMLElement;
    const $showing = document.querySelector('#dt-showing') as HTMLElement;
    const $pageLabel = document.querySelector('#dt-page-label') as HTMLElement;
    const $prev = document.querySelector('#dt-prev') as HTMLButtonElement;
    const $next = document.querySelector('#dt-next') as HTMLButtonElement;
    const $retry = document.querySelector('#dt-retry') as HTMLButtonElement;
    const brand = $root.dataset.brand || '';
    const teamFallback = brand ? `${brand} team` : 'our team';

    let tickets: models.HyejooQuoteRequestProjectTask[] = [];
    let q = QueryString().get('q')?.trim() || '';
    let status = parseStatus(QueryString().get('status'));
    let sort: 'new' | 'old' = QueryString().get('sort') === 'old' ? 'old' : 'new';
    let page = parsePage(QueryString().get('page'));
    let pageSize = parsePageSize(QueryString().get('max'));
    let refreshTimer = 0;

    $search.value = q;
    $sort.value = sort;
    $pageSize.value = String(pageSize);

    $search.addEventListener('input', () => {
        q = $search.value.trim();
        page = 1;
        // No scroll — typing happens with the controls already in view.
        scheduleRefresh(false);
    });
    $sort.addEventListener('change', () => {
        sort = $sort.value === 'old' ? 'old' : 'new';
        page = 1;
        scheduleRefresh();
    });
    $pageSize.addEventListener('change', () => {
        pageSize = parsePageSize($pageSize.value);
        page = 1;
        scheduleRefresh();
    });
    $prev.addEventListener('click', () => {
        page = Math.max(1, page - 1);
        scheduleRefresh();
    });
    $next.addEventListener('click', () => {
        page += 1;
        scheduleRefresh();
    });
    $clearFilters.addEventListener('click', () => {
        q = '';
        status = '';
        page = 1;
        $search.value = '';
        scheduleRefresh();
    });
    $retry.addEventListener('click', () => {
        void load();
    });

    await load();

    async function load() {
        showLoading();
        try {
            tickets = await fetchAllTickets();
            if (tickets.length === 0) {
                showEmpty();
                return;
            }
            showMain();
            render();
        } catch {
            showError();
        }
    }

    async function fetchAllTickets() {
        const rows: models.HyejooQuoteRequestProjectTask[] = [];
        let p = 0;
        for (;;) {
            const resp = await axios.get('/quote-request-project-tasks', {
                params: {
                    sort: 'created desc',
                    max: 100,
                    p,
                    data: dataFields,
                },
            });
            const data = models.QuoteRequestProjectTasksHyejooResponse.fromJSON(resp.data);
            rows.push(...data.quoteRequestProjectTasks);
            if (!data.nextPage) {
                break;
            }
            p += 1;
        }
        return rows;
    }

    function scheduleRefresh(scrollToTop = true) {
        $list.classList.add('dtStale');
        $refresh.classList.remove('d-none');
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(() => {
            render();
            $list.classList.remove('dtStale');
            $refresh.classList.add('d-none');
            if (scrollToTop) {
                // 'instant', not 'smooth'/'auto': the theme sets scroll-behavior
                // smooth on <html>, and the re-render's layout pass cancels
                // in-flight smooth scrolls (scroll anchoring), so an animated
                // scroll here never lands.
                $root.scrollIntoView({ behavior: 'instant' });
            }
        }, refreshMs);
    }

    function render() {
        const counts = countBuckets(tickets);
        const quotesWaiting = counts.sent;
        $subtitle.textContent = subtitleText(tickets.length, quotesWaiting);
        $subtitle.classList.remove('d-none');

        drawChips(counts);

        const filtered = applyFilters(tickets);
        const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
        if (page > totalPages) {
            page = totalPages;
        }
        if (page < 1) {
            page = 1;
        }

        persistQuery();

        $list.replaceChildren();
        if (filtered.length === 0) {
            $noResults.classList.remove('d-none');
            $clearFilters.textContent = `Show all ${tickets.length} tickets`;
            $pager.classList.add('d-none');
            return;
        }

        $noResults.classList.add('d-none');
        const start = (page - 1) * pageSize;
        const pageRows = filtered.slice(start, start + pageSize);
        for (const task of pageRows) {
            $list.appendChild(drawCard(task, teamFallback));
        }

        const from = start + 1;
        const to = start + pageRows.length;
        $showing.textContent = `Showing ${from}\u2013${to} of ${filtered.length}`;
        $pageLabel.textContent = `Page ${page} of ${totalPages}`;
        $prev.disabled = page <= 1;
        $next.disabled = page >= totalPages;
        $pager.classList.remove('d-none');
    }

    function drawChips(counts: Record<statusFilter, number>) {
        $chips.replaceChildren();
        for (const chip of statusFilters) {
            const $chip = template.Clone('#dt-chip-template').firstElementChild as HTMLButtonElement;
            const n = chip.filter === '' ? tickets.length : counts[chip.filter];
            $chip.textContent = `${chip.label} (${n})`;
            $chip.classList.toggle('active', status === chip.filter);
            $chip.setAttribute('aria-pressed', String(status === chip.filter));
            $chip.addEventListener('click', () => {
                status = chip.filter;
                page = 1;
                scheduleRefresh();
            });
            $chips.appendChild($chip);
        }
    }

    function applyFilters(all: models.HyejooQuoteRequestProjectTask[]) {
        const needle = q.toLowerCase();
        const filtered = all.filter(task => {
            if (status && bucketOf(task).filter !== status) {
                return false;
            }
            if (needle && !displayName(task).toLowerCase().includes(needle)) {
                return false;
            }
            return true;
        });
        filtered.sort((a, b) => {
            const am = a.created.isValid ? a.created.toMillis() : 0;
            const bm = b.created.isValid ? b.created.toMillis() : 0;
            return sort === 'old' ? am - bm : bm - am;
        });
        return filtered;
    }

    function persistQuery() {
        const qs = QueryString();
        if (q) {
            qs.set('q', q);
        } else {
            qs.delete('q');
        }
        if (status) {
            qs.set('status', status);
        } else {
            qs.delete('status');
        }
        if (sort === 'old') {
            qs.set('sort', 'old');
        } else {
            qs.delete('sort');
        }
        if (page > 1) {
            qs.set('page', String(page));
        } else {
            qs.delete('page');
        }
        if (pageSize !== defaultPageSize) {
            qs.set('max', String(pageSize));
        } else {
            qs.delete('max');
        }
    }

    function showLoading() {
        $loading.classList.remove('d-none');
        $error.classList.add('d-none');
        $empty.classList.add('d-none');
        $main.classList.add('d-none');
        $subtitle.classList.add('d-none');
    }

    function showError() {
        $loading.classList.add('d-none');
        $error.classList.remove('d-none');
        $empty.classList.add('d-none');
        $main.classList.add('d-none');
        $subtitle.classList.add('d-none');
    }

    function showEmpty() {
        $loading.classList.add('d-none');
        $error.classList.add('d-none');
        $empty.classList.remove('d-none');
        $main.classList.add('d-none');
        $subtitle.classList.add('d-none');
    }

    function showMain() {
        $loading.classList.add('d-none');
        $error.classList.add('d-none');
        $empty.classList.add('d-none');
        $main.classList.remove('d-none');
    }
}

function parseStatus(raw: string | undefined): statusFilter {
    switch (raw) {
    case 'queue':
    case 'progress':
    case 'art':
    case 'sent':
    case 'canceled':
        return raw;
    default:
        return '';
    }
}

function parsePage(raw: string | undefined) {
    const n = parseInt(raw || '1', 10);
    return n > 0 ? n : 1;
}

function parsePageSize(raw: string | undefined) {
    const n = parseInt(raw || '', 10);
    return pageSizes.includes(n) ? n : defaultPageSize;
}

function bucketOf(task: models.HyejooQuoteRequestProjectTask) {
    // The badge, the chip counts and the quote button must agree on whether a quote was sent, and only a real send says so.
    return statusDisplay[TicketEffectiveStatusID(task.quoteRequestProjectTaskStatusID, Boolean(task.listSummary?.latestQuoteSentKey))] ?? inTheQueue;
}

function displayName(task: models.HyejooQuoteRequestProjectTask) {
    return task.listSummary?.name || 'Design Ticket';
}

function countBuckets(all: models.HyejooQuoteRequestProjectTask[]) {
    const counts: Record<statusFilter, number> = {
        '': all.length,
        queue: 0,
        progress: 0,
        art: 0,
        sent: 0,
        canceled: 0,
    };
    for (const task of all) {
        counts[bucketOf(task).filter] += 1;
    }
    return counts;
}

function subtitleText(n: number, quotesWaiting: number) {
    let text = `${n} design project${n === 1 ? '' : 's'}`;
    if (quotesWaiting > 0) {
        text += ` · ${quotesWaiting} quote${quotesWaiting === 1 ? ' is' : 's are'} ready for your review`;
    }
    return text;
}

function drawCard(task: models.HyejooQuoteRequestProjectTask, teamFallback: string) {
    const $card = template.Clone('#dt-card-template').firstElementChild as HTMLElement;
    const bucket = bucketOf(task);
    const canceled = bucket.filter === 'canceled';
    const href = `${Relref('/ticket')}?qrpt=${task.key}`;
    const name = displayName(task);

    const $name = $card.querySelector('.dt-name') as HTMLAnchorElement;
    $name.textContent = name;
    $name.href = href;
    if (canceled) {
        $name.classList.add('dtNameX');
    }

    const $badge = $card.querySelector('.dt-badge') as HTMLElement;
    $badge.textContent = bucket.label;
    $badge.classList.add(...bucket.badge.split(' '));

    const $typeLine = $card.querySelector('.dt-type-line') as HTMLElement;
    $typeLine.textContent = typeLine(task);

    const $eta = $card.querySelector('.dt-eta') as HTMLElement;
    const $etaText = $card.querySelector('.dt-eta-text') as HTMLElement;
    const eta = etaText(task, bucket.filter);
    if (eta) {
        $etaText.textContent = eta;
        $eta.classList.remove('d-none');
    }

    const $etaSub = $card.querySelector('.dt-eta-sub') as HTMLElement;
    const sub = etaSubText(task, bucket.filter);
    if (sub) {
        $etaSub.textContent = sub;
        $etaSub.classList.remove('d-none');
    }

    const $submitted = $card.querySelector('.dt-submitted') as HTMLElement;
    $submitted.textContent = task.created.isValid ?
        `Submitted ${task.created.toLocaleString(DateTime.DATE_MED)}` :
        'Submitted';

    const $needBy = $card.querySelector('.dt-need-by') as HTMLElement;
    const dateNeeded = task.listSummary?.dateNeeded;
    $needBy.textContent = dateNeeded?.isValid ?
        `Need by ${dateNeeded.toLocaleString(DateTime.DATE_MED)}` :
        'Need-by date flexible';

    const $designer = $card.querySelector('.dt-designer') as HTMLElement;
    if (canceled) {
        $designer.textContent = 'Can be reopened from the ticket';
    } else if (task.assignedUser?.firstName) {
        $designer.textContent = `Designer: ${task.assignedUser.firstName}`;
    } else {
        $designer.textContent = 'Designer not yet assigned';
    }

    const $priority = $card.querySelector('.dt-priority') as HTMLElement;
    const $priorityText = $card.querySelector('.dt-priority-text') as HTMLElement;
    const depositDate = prioritizedDepositDate(task);
    if (depositDate) {
        const when = depositDate.isValid ? depositDate.toLocaleString(DateTime.DATE_MED) : '';
        $priorityText.textContent = `Prioritized — deposit received ${when}; your ticket jumped to the front of the line`;
        $priority.classList.remove('d-none');
    }

    const $thumbLink = $card.querySelector('.dt-thumb-link') as HTMLAnchorElement;
    const $thumbImg = $card.querySelector('.dt-thumb') as HTMLImageElement;
    const $placeholder = $card.querySelector('.dt-thumb-placeholder') as HTMLElement;
    $thumbLink.href = href;
    const thumbSrc = thumbnailSrc(task);
    if (thumbSrc) {
        $placeholder.classList.add('d-none');
        if (canceled) {
            $thumbLink.classList.add('dtImgX');
        }
        $thumbImg.alt = name;
        void Picture($thumbImg, thumbSrc, {
            displayWidth: 84,
            sizes: '(max-width: 575.98px) 64px, 84px',
            alt: name,
        });
    } else {
        $thumbLink.classList.add('d-none');
        $placeholder.classList.remove('d-none');
    }

    const $activity = $card.querySelector('.dt-activity') as HTMLElement;
    const event = task.lastUserEvent;
    if (event && (event.content || event.id || event.created.isValid)) {
        const $who = $card.querySelector('.dt-activity-who') as HTMLElement;
        const $when = $card.querySelector('.dt-activity-when') as HTMLElement;
        const $snippet = $card.querySelector('.dt-activity-snippet') as HTMLElement;
        $who.textContent = event.fromAdmin ? event.user?.firstName || teamFallback : 'You';
        $when.textContent = activityWhen(event.created);
        // Upload-only events have no text \u2014 drop the snippet and its
        // separator instead of rendering a dangling empty quote.
        const snippet = html.UnHTML(event.content).replace(/\s+/g, ' ').trim();
        if (snippet) {
            $snippet.textContent = `\u201C${snippet}\u201D`;
        } else {
            $snippet.classList.add('d-none');
            ($card.querySelector('.dt-activity-snippet-sep') as HTMLElement).classList.add('d-none');
        }
        $activity.classList.remove('d-none');
    }

    const $quoteBtn = $card.querySelector('.dt-quote-btn') as HTMLAnchorElement;
    const quoteKey = task.listSummary?.latestQuoteSentKey || '';
    if (bucket.filter === 'sent' && quoteKey) {
        $quoteBtn.href = FinishedQuoteLink(quoteKey);
        $quoteBtn.classList.remove('d-none');
    }

    const $view = $card.querySelector('.dt-view-btn') as HTMLAnchorElement;
    $view.href = href;

    return $card;
}

function typeLine(task: models.HyejooQuoteRequestProjectTask) {
    const typeLabel = typeLabels[task.quoteRequestProjectTaskTypeID] ?? 'New';
    const parts = [typeLabel];
    const product = task.listSummary?.product || '';
    if (product) {
        parts.push(product);
    }
    const quantity = task.listSummary?.quantity || 0;
    if (quantity) {
        parts.push(quantity.toLocaleString());
    }
    return parts.join(' · ');
}

function etaText(task: models.HyejooQuoteRequestProjectTask, filter: statusFilter) {
    if (filter !== 'queue' && filter !== 'progress' && filter !== 'art') {
        return '';
    }
    if (!task.estimatedCompletion.isValid) {
        return '';
    }
    const today = DateTime.local().startOf('day');
    const completion = task.estimatedCompletion.startOf('day');
    if (completion <= today) {
        return 'Expected back today';
    }
    return `Expected back ${task.estimatedCompletion.toLocaleString(DateTime.DATE_MED)}`;
}

function etaSubText(task: models.HyejooQuoteRequestProjectTask, filter: statusFilter) {
    if (filter === 'sent' || filter === 'canceled') {
        return '';
    }
    if (!task.estimatedCompletion.isValid) {
        return '';
    }
    // Branch on the raw value — rounding first would call 1.4h "under an hour".
    if (task.estimatedHoursRemaining < 1) {
        return 'Under an hour of working time left';
    }
    const hours = Math.round(task.estimatedHoursRemaining);
    return `About ${hours} business hour${hours === 1 ? '' : 's'} of working time left`;
}

function prioritizedDepositDate(task: models.HyejooQuoteRequestProjectTask) {
    let date: DateTime | undefined;
    for (const log of task.changelog) {
        if (log.column === 'QuoteRequestProjectTaskPriorityID' &&
            log.newValue === models.QuoteRequestProjectTaskPriorityID.QUOTE_REQUEST_PROJECT_TASK_PRIORITIES_PREPAID) {
            if (log.time.isValid && (!date || log.time > date)) {
                date = log.time;
            }
        }
    }
    const prepaid = task.quoteRequestProjectTaskPriorityID ===
        models.QuoteRequestProjectTaskPriorityID.QUOTE_REQUEST_PROJECT_TASK_PRIORITIES_PREPAID;
    if (!date && !prepaid) {
        return undefined;
    }
    return date ?? task.created;
}

function thumbnailSrc(task: models.HyejooQuoteRequestProjectTask) {
    const proof = task.quoteProof?.proof;
    if (proof) {
        const urls = proof.getURLs();
        if (urls.main) {
            return urls.main;
        }
    }
    const upload = task.listSummary?.firstUpload;
    if (upload?.key) {
        return upload.url();
    }
    return '';
}

function activityWhen(created: DateTime) {
    if (!created.isValid) {
        return '';
    }
    const hours = Math.abs(created.diffNow('hours').hours);
    if (hours < 48) {
        return created.toRelative() || created.toLocaleString(DateTime.DATE_MED);
    }
    return created.toLocaleString(DateTime.DATE_MED);
}
